import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Collapse,
  TableCell,
  TableContainer,
  TableRow,
} from "@material-ui/core";
import HearingIcon from "@material-ui/icons/Hearing";
import NoteIcon from "@material-ui/icons/Note";
import ListeningDialog from "../listenings/ListeningDialog";
import { buildRawListeningOwner } from "../listenings/listeningOwners";
import CalibrationNotesDialog from "../notes/CalibrationNotesDialog";
import useCalibrationNotes from "../notes/useCalibrationNotes";
import ActionCountCell from "../shared/ActionCountCell";
import MicrophoneCalibrationStatus, {
  microphoneCalibrationColumnStyle,
} from "../shared/MicrophoneCalibrationStatus";
import RawCalibrationDetailsTable from "../shared/RawCalibrationDetailsTable";
import { hasMicrophoneCalibration } from "../shared/microphoneCalibrationUtils";
import { convertDate, convertHours } from "../utils/dateConverter";
import {
  addRawNote,
  deleteRawNote,
  getRawNotes,
  updateRawNote,
} from "../utils/NetworkHandler.js";

export default function FilteredRow({
  id,
  rowData,
  removeHandler,
  isDetailedData,
  updateData,
}) {
  const [open, setOpen] = useState(false);
  const [listeningDialogOpen, setListeningDialogOpen] = useState(false);
  const [listeningCount, setListeningCount] = useState(
    rowData?.listeningCount != null ? rowData.listeningCount : 0,
  );

  useEffect(() => {
    setListeningCount(
      rowData?.listeningCount != null ? rowData.listeningCount : 0,
    );
  }, [rowData?.listeningCount]);

  const rawListeningOwner = useMemo(
    () =>
      buildRawListeningOwner({
        id,
        brand: rowData?.brand,
        model: rowData?.model,
        deviceId: rowData?.deviceId,
        calibrations: rowData?.calibrations,
      }),
    [
      id,
      rowData?.brand,
      rowData?.model,
      rowData?.deviceId,
      rowData?.calibrations,
    ],
  );

  const initialNotesCountsByOwnerId = useMemo(
    () => ({
      [id]: rowData?.notes?.size != null ? rowData.notes.size : 0,
    }),
    [id, rowData?.notes?.size],
  );

  const notes = useCalibrationNotes({
    getOwnerId: () => id,
    getNotes: getRawNotes,
    addNote: addRawNote,
    updateNote: updateRawNote,
    deleteNote: deleteRawNote,
    initialCountsByOwnerId: initialNotesCountsByOwnerId,
    onNotesChanged: async () => {
      if (updateData) {
        await updateData();
      }
    },
  });

  const rawNotesCount = notes.getNotesCount(rowData, rowData?.notes?.size || 0);

  const handleListeningCreated = () => {
    setListeningCount((prev) => prev + 1);

    if (updateData) {
      Promise.resolve(updateData()).catch((error) => {
        console.error("updateData failed after listening create", error);
      });
    }
  };

  const handleListeningDeleted = () => {
    setListeningCount((prev) => Math.max(0, prev - 1));

    if (updateData) {
      Promise.resolve(updateData()).catch((error) => {
        console.error("updateData failed after listening delete", error);
      });
    }
  };

  return (
    <>
      <TableRow>
        <TableCell style={{ paddingLeft: "30px" }}>
          {convertHours(rowData.createdAt)}
        </TableCell>
        <TableCell>{convertDate(rowData.createdAt)}</TableCell>
        <TableCell>{rowData.model}</TableCell>
        <TableCell>{rowData.brand}</TableCell>
        <TableCell>{rowData.manufacturer}</TableCell>
        <TableCell>{rowData.calibratorName}</TableCell>
        <TableCell
          align="center"
          style={microphoneCalibrationColumnStyle}
        >
          <MicrophoneCalibrationStatus
            available={hasMicrophoneCalibration(rowData)}
          />
        </TableCell>

        <ActionCountCell
          label="Notatki"
          icon={NoteIcon}
          onClick={() => notes.openNotesDialog(rowData)}
          count={rawNotesCount}
          hideZeroCount
        />

        <ActionCountCell
          label="Odsłuchy"
          icon={HearingIcon}
          onClick={() => setListeningDialogOpen(true)}
          count={listeningCount}
        />

        <TableCell align="center">
          <Button
            onClick={() => setOpen((prevOpen) => !prevOpen)}
            style={{ marginRight: 8 }}
          >
            Szczegóły
          </Button>
          <Button onClick={() => removeHandler(id)}>Usuń</Button>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <TableContainer>
              <RawCalibrationDetailsTable
                calibrations={rowData?.calibrations}
                microphoneCalibration={rowData?.microphoneCalibration}
                isDetailedData={isDetailedData}
              />
            </TableContainer>
          </Collapse>
        </TableCell>
      </TableRow>

      <ListeningDialog
        open={listeningDialogOpen}
        onClose={() => setListeningDialogOpen(false)}
        ownerCalibrationId={rawListeningOwner.ownerCalibrationId}
        averagingLevel={rawListeningOwner.averagingLevel}
        defaultDeviceId={rawListeningOwner.deviceId}
        defaultModelName={rawListeningOwner.model}
        defaultBrandName={rawListeningOwner.brand}
        availableFrequencies={rawListeningOwner.availableFrequencies}
        onListeningCreated={handleListeningCreated}
        onListeningDeleted={handleListeningDeleted}
      />

      <CalibrationNotesDialog
        open={notes.open}
        onClose={notes.closeNotesDialog}
        title="Notatki kalibracji"
        notes={notes.notes}
        loading={notes.loadingNotes}
        error={notes.notesError}
        editingNoteId={notes.editingNoteId}
        editingText={notes.editingText}
        onEditingTextChange={notes.setEditingText}
        saving={notes.saving}
        deletingId={notes.deletingId}
        newNoteText={notes.newNoteText}
        onNewNoteTextChange={notes.setNewNoteText}
        adding={notes.adding}
        onStartEdit={notes.startEdit}
        onCancelEdit={notes.cancelEdit}
        onSaveEdit={notes.saveEdit}
        onDeleteNote={notes.handleDeleteNote}
        onStartAdd={notes.startAdd}
        onCancelAdd={notes.cancelAdd}
        onSaveNew={notes.saveNew}
        emptyText="Brak notatek."
      />
    </>
  );
}
